import { centsToDollars } from "@outschool/business-rules";
import { useCurrencyLocalization } from "@outschool/localization";
import React from "react";

import pkg from "../../lib/pkg";
import { AnalyticsError } from "../../lib/util";
import { IntegrationCategory } from "../../providers/AnalyticsContext";
import useTrackEvent from "../useTrackEvent";
import { mapActivityListingToProduct } from "./util";

import type { ActivityListing, Product } from "./types";

const EVENT_NAME = "Product Viewed";

type ProductViewedLegacy = {
  uid?: string;
  leader_name?: string;
  content_name?: string;
};

interface ProductViewedProperties extends Product, ProductViewedLegacy {
  coupon?: string;
  currency: string;
  value: number;
}

export default function useProductViewed() {
  const { currencyCode } = useCurrencyLocalization();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const [tracked, setTracked] = React.useState<boolean>(false);

  function track(activityListing: ActivityListing) {
    try {
      if (!activityListing || tracked) {
        return;
      }

      const productMapped: Product =
        mapActivityListingToProduct(activityListing);

      // additional properties for backwards compatibility
      const productLegacy: ProductViewedLegacy = {
        uid: activityListing.activity.uid,
        leader_name: activityListing.leader.name ?? undefined,
        content_name: activityListing.activity.title,
      };

      const properties: ProductViewedProperties = {
        ...productLegacy,
        ...productMapped,
        currency: <string>currencyCode,
        value: centsToDollars(activityListing.activity.price_cents),
      };

      trackAdEvent(EVENT_NAME, properties);

      setTracked(true);
    } catch (error) {
      pkg.onError(new AnalyticsError(EVENT_NAME, error));
    }
  }

  return React.useCallback(track, [currencyCode, trackAdEvent, tracked]);
}
